import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Map, LayersControl, ZoomControl, FeatureGroup } from 'react-leaflet';
import { latLngBounds, latLng } from 'leaflet';
import { GoogleLayer } from 'react-leaflet-google';
import VectorGridLayer from './Layers/VectorGridLayer';
import DemographicsLayer from './Layers/Demographics';
import PredefinedLayer from './Layers/Predefined';
import CustomLayer from './Layers/Custom';
import ProfileLayer from './Layers/Profile';
import WorkforceLayer from './Layers/Workforce';
import isBadCoordinate from '../../utils/isBadCoordinate';
import MeasureDistance from './MeasureDistance';
import BusinessMapMarker from './BusinessMapMarker';
import DrawShape from './DrawShape';
import MapMarkers from './MapMarkers';
import AnalysisPinDropper from './AnalysisPinDropper';
import './Map.css';
import { API_KEY, endpoints, storage, railroads } from '../../config';

const { BaseLayer } = LayersControl;

export class MapComponent extends Component {
  constructor(props) {
    super(props);

    this.state = {
      mapView: '',
      bounds: null,
      zoom: Math.clamp(this.props.defaultZoom, -2, 2),
      hasInterracted: false,
      addLayers: false,
      layersFromProps: this.props.layers,
      propertyType: this.props.type,
    };

    this.setInitialMapView = this.setInitialMapView.bind(this);
    this.setBoundsFromProperties = this.setBoundsFromProperties.bind(this);
    this.updateBounds = this.updateBounds.bind(this);
    this.onInterract = this.onInterract.bind(this);
    this.updateAddLayers = this.updateAddLayers.bind(this);
    this.updateLayersFromProps = this.updateLayersFromProps.bind(this);
  }

  componentDidMount() {
    const { properties } = this.props;
    if (properties && properties.length && !this.state.bounds) {
      this.setBoundsFromProperties(properties);
    }

    // Register event on leaflet map to set interracted when someone drags it
    if (this.mapRef.leafletElement) {
      this.mapRef.leafletElement.once({
        drag: this.onInterract,
      });
    }
  }

  componentWillReceiveProps({ layers, mapType, polygons, properties, type }) {
    if (mapType !== this.state.mapView && this.state.ready) {
      this.setState(prevState => ({
        ...prevState,
        mapView: mapType,
      }));
    }
    if (layers && layers.size > 0) {
      this.setState(prevState => ({
        ...prevState,
        layersFromProps: this.props.layers,
      }));
    }
    if (polygons && polygons.length) {
      this.setBoundsFromPolygons();
    }
    if (properties && properties.length && !this.state.bounds) {
      this.setBoundsFromProperties(properties);
    }

    /*
      This block resets the map bounds to new properties fetched after changing the route.
      This lifecycle method cycles several times before properties are finally fetched so
      cannot rely on compare of props/nextProps for the "type". New state value was created
      to hold the initial type and once properties are fetched uses this to compare if
      bounds should be reset (ie. there are properties and the property type has changed).
    */
    const { defaultOutline } = this.props.profile;
    const noDefaultOutline = defaultOutline === undefined || defaultOutline === 'none';
    if (properties && properties.length && this.state.propertyType !== type && noDefaultOutline) {
      if (polygons && polygons.length) {
        this.setBoundsFromPolygons();
      } else {
        this.setBoundsFromProperties(properties);
      }
      this.setState({
        propertyType: type,
      });
    }
  }

  componentWillUnmount() {
    if (this.pendingMapView) {
      clearTimeout(this.pendingMapView);
    }
  }

  onInterract() {
    this.setState({ hasInterracted: true });
  }

  setBoundsFromProperties(properties) {
    const coordinates = properties.reduce((map, property) => {
      if (property.coordinates !== undefined) {
        const coords = property.coordinates.map(coordinate => parseFloat(coordinate));
        map.push([coords[1], coords[0]]);
      }
      return map;
    }, [])
    .filter(bound => {
      return !isBadCoordinate(bound[0]) || !isBadCoordinate(bound[1]);
    });
    if (coordinates.length) {
      this.updateBounds(coordinates);
    }
  }

  setBoundsFromPolygons() {
    setImmediate(() => {
      const bounds = (
        this.polygonGroup &&
        this.polygonGroup.leafletElement &&
        this.polygonGroup.leafletElement.getBounds()
      );
      if (bounds.isValid()) {
        this.setState({ bounds });
      }
    });
  }

  setInitialMapView() {
    this.pendingMapView = setTimeout(() => {
      this.setState(prevState => ({
        ...prevState,
        mapView: this.props.mapType,
        ready: true,
      }), () => {
        this.pendingMapView = null;
        this.updateAddLayers(true);
      });
    }, 1000);
  }

  updateAddLayers(status) {
    this.setState({ addLayers: status });
  }

  updateLayersFromProps() {
    this.setState({ layersFromProps: this.props.layers });
  }

  updateBounds(coordinates) {
    const { hasInterracted, zoom } = this.state;

    if (hasInterracted) {
      return;
    }

    let bounds;
    if (Array.isArray(coordinates)) {
      bounds = coordinates.length > 1
      ? latLngBounds(coordinates)
      : latLng(coordinates[0]).toBounds(500);
    } else {
      bounds = coordinates;
    }

    if (zoom) {
      const curZoom = this.mapRef.leafletElement.getBoundsZoom(bounds);
      this.mapRef.leafletElement.setView(bounds.getCenter(), curZoom + zoom);
      bounds = this.mapRef.leafletElement.getBounds();
    }

    this.setState({
      bounds,
    });
  }

  render() {
    const {
      appliedSearchOpen,
      layers,
      mapTypes,
      organization,
      polygons,
      profile,
      properties,
      type,
      zoom,
      handleModalOpen,
      onViewportChanged,
      center,
      setViewerPolygon,
      businesses,
      markers,
      mapChildren,
      allLayers,
      mapLoading,
    } = this.props;
    const { mapView } = this.state;
    const appliedSearchOpenClass = appliedSearchOpen ? 'map-applied-search-open' : 'map-applied-search-closed';
    const boundsProp = this.state.bounds ? { bounds: this.state.bounds } : {};
    const env = process.env.REACT_APP_API || 'local';
    const { url } = endpoints[env];
    const { setInitialMapView } = this;
    const defaultCustomLayers = [];
    const setLayers = this.state.addLayers;
    if (allLayers.custom && allLayers.custom.length > 0) {
      allLayers.custom.forEach(layer => {
        if (layer.showByDefault) {
          defaultCustomLayers.push(layer);
        }
      });
    }

    return (
      <div className={`map ${appliedSearchOpenClass}`}>
        {mapLoading &&
          <div className="loading-overlay">
            <img
              alt="loading gif"
              src={`${storage.distPath}/loading.gif`}
            />
          </div>
        }
        <Map
          center={center}
          zoom={zoom}
          zoomControl={false}
          scrollWheelZoom
          ref={map => { this.mapRef = map; }}
          whenReady={function whenReady() {
            setInitialMapView();
            onViewportChanged({ bounds: this.getBounds() });
          }}
          onViewportChanged={viewport => {
            if (this.mapRef) {
              onViewportChanged({ ...viewport, bounds: this.mapRef.leafletElement.getBounds() });
            }
          }}
          {...boundsProp}
        >
          {setLayers && layers.get('demographics') &&
          <DemographicsLayer
            demographics={layers.get('demographics')}
            zoom={zoom}
          />}
          {setLayers && layers.get('workforce') &&
          <WorkforceLayer
            workforce={layers.get('workforce')}
            zoom={zoom}
          />}
          {setLayers && (layers.get('customLayer') || []).map(layer =>
            <CustomLayer
              key={layer.id}
              onBoundsUpdate={this.updateBounds}
              shapefile={layer}
            />,
          )}
          {setLayers && defaultCustomLayers.map((layer, index) =>
            <CustomLayer
              key={layer.id + index.toString()}
              onBoundsUpdate={this.updateBounds}
              shapefile={layer}
            />,
          )}
          {setLayers && (layers.get('predefinedLayer') || []).map(layer => {
            if (Object.keys(railroads).map(k => k.toLowerCase()).includes(layer.toLowerCase())) {
              if (layer.toLowerCase() !== 'cpkc') {
                return <VectorGridLayer key={layer} url={`${url}/geo/${railroads[layer].url}/railroad/v1/{z}/{x}/{y}.pbf`} />;
              }
              return <VectorGridLayer key={layer} url={`${railroads[layer].url}/tile/{z}/{y}/{x}.pbf`} />;
            }
            return <PredefinedLayer layer={layer} key={layer} markers={markers} {...boundsProp} />;
          })}
          <ZoomControl position="bottomright" />
          <MeasureDistance />
          <DrawShape propertyType={type} />
          <AnalysisPinDropper />

          <LayersControl position="topleft">
            <BaseLayer checked={mapView === mapTypes.ROADMAP || mapView === mapTypes.STREET} name="Roads">
              <GoogleLayer googlekey={API_KEY} maptype={mapTypes.ROADMAP} libraries={['geometry', 'places']} />
            </BaseLayer>
            <BaseLayer checked={mapView === mapTypes.TERRAIN} name="Terrain">
              <GoogleLayer googlekey={API_KEY} maptype={mapTypes.TERRAIN} libraries={['geometry', 'places']} />
            </BaseLayer>
            <BaseLayer checked={mapView === mapTypes.SATELLITE} name="Satellite">
              <GoogleLayer googlekey={API_KEY} maptype={mapTypes.SATELLITE} libraries={['geometry', 'places']} />
            </BaseLayer>
            <BaseLayer checked={mapView === mapTypes.HYBRID} name="Hybrid">
              <GoogleLayer googlekey={API_KEY} maptype={mapTypes.HYBRID} libraries={['geometry', 'places']} />
            </BaseLayer>
            <BaseLayer check={mapView === mapTypes.RAILROAD} name="Railroad">
              <GoogleLayer googlekey={API_KEY} maptype={mapTypes.RAILROAD} libraries={['geometry', 'places']} />
            </BaseLayer>
          </LayersControl>

          {properties &&
            <MapMarkers properties={properties} type={type} handleModalOpen={handleModalOpen} />
          }
          {businesses && businesses.map((business, index) =>
            <BusinessMapMarker business={business} key={`business-${index}`} markers={markers} />,
          )}
          <FeatureGroup ref={group => { this.polygonGroup = group; }}>
            {polygons}
          </FeatureGroup>
          {
            profile &&
            profile.defaultOutline &&
            !polygons &&
            (type === 'buildings' || type === 'sites') &&
            <ProfileLayer
              organization={organization}
              profile={profile}
              onBoundsUpdate={bounds => {
                this.updateBounds(bounds);
                this.profileBounds = bounds;
              }}
              setViewerPolygon={setViewerPolygon}
            />
          }
          {mapChildren}
        </Map>
      </div>
    );
  }
}

MapComponent.propTypes = {
  properties: PropTypes.arrayOf(PropTypes.object),
  type: PropTypes.string,
  layers: PropTypes.object,
  zoom: PropTypes.number,
  defaultZoom: PropTypes.number,
  appliedSearchOpen: PropTypes.bool,
  mapType: PropTypes.string,
  mapTypes: PropTypes.object,
  polygons: PropTypes.arrayOf(PropTypes.element),
  organization: PropTypes.string,
  profile: PropTypes.object,
  handleModalOpen: PropTypes.func,
  onViewportChanged: PropTypes.func,
  center: PropTypes.arrayOf(PropTypes.number),
  setViewerPolygon: PropTypes.func,
  businesses: PropTypes.arrayOf(PropTypes.object),
  markers: PropTypes.object,
  mapChildren: PropTypes.arrayOf(PropTypes.node),
  allLayers: PropTypes.object,
  mapLoading: PropTypes.bool,
};

MapComponent.defaultProps = {
  mapChildren: [],
};

export default MapComponent;



// WEBPACK FOOTER //
// ./src/components/Map/Map.js